"use client";

import { useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { useNewBuildAvailable } from "./useNewBuildAvailable";

const DISMISS_KEY = "plantista_update_dismissed_build";

function readDismissed(): string | null {
  try {
    return sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

/**
 * Bottom sheet shown when a newer build has been deployed. Same look and
 * behaviour as the Plantista dashboard's banner (plantista/web): "Later" hides
 * it for that build in this tab; a newer deploy brings it back.
 */
export function UpdateAvailableBanner() {
  const newBuild = useNewBuildAvailable();
  // Lazy initialiser: the component renders nothing until a poll finds a new
  // build, which only happens client-side, so sessionStorage is available.
  const [dismissed, setDismissed] = useState<string | null>(() =>
    typeof window === "undefined" ? null : readDismissed()
  );

  if (!newBuild || dismissed === newBuild.buildId) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, newBuild.buildId);
    } catch {
      // storage blocked: dismiss in memory only
    }
    setDismissed(newBuild.buildId);
  };

  return (
    <div
      role="alertdialog"
      aria-live="polite"
      aria-labelledby="update-available-title"
      style={{ zIndex: 2147483100 }}
      className="fixed inset-x-4 bottom-4 mx-auto max-w-xl rounded-xl border border-neutral-200 bg-white text-neutral-950 shadow-lg md:bottom-6 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-50"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-lime/15 text-lime">
          <RefreshCw className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p id="update-available-title" className="text-sm font-semibold">
            A new version of the designer is available
            {newBuild.version ? ` (v${newBuild.version})` : ""}
          </p>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Refresh the page to update. Save anything you&apos;re working on
            first.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md border border-lime bg-lime px-4 py-2 text-sm font-medium text-white transition-colors hover:border-lime-dark hover:bg-lime-dark"
            >
              Refresh now
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-md border border-lime bg-white px-4 py-2 text-sm font-medium text-lime transition-colors hover:bg-lime/10 dark:bg-transparent"
            >
              Later
            </button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="rounded p-1 text-neutral-500 hover:text-neutral-950 dark:text-neutral-400 dark:hover:text-neutral-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default UpdateAvailableBanner;
